import _ from "lodash";
import AuthProvider from "../../authentication/authService";
import {
  getCSRFToken,
  getOrgStatus,
  setLoading,
  setSignalR,
  setVmIdNotification,
} from "../../../features/store/action";
import {
  getCourseViewTemplate,
  getCardCourseCount,
} from "../../../features/components/academy/view-course-templates/store/view-course-template.action";
import {
  getCourseDataById,
  setVmPowerOffProgress,
  setVmPowerOnProgress,
  resetVmPowerOperation,
  insertingMedia,
  ejectingMedia,
  resetMediaOperation,
} from "../../../features/components/academy/create-course/store/create-course.action";
import { useDispatch } from "react-redux";
import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import config from "../../authentication/config";
import { customToast } from "../../../App";
import { INotification } from "../../../features/store/model";

const MAX_TRACKED_NOTIFICATIONS = 1000;
const seenNotificationIds = new Set<string>();
const notificationIdOrder: string[] = [];

const isDuplicateNotification = (notificationId: any): boolean => {
  if (!notificationId) return false;
  const id = String(notificationId);
  if (seenNotificationIds.has(id)) return true;
  seenNotificationIds.add(id);
  notificationIdOrder.push(id);
  if (notificationIdOrder.length > MAX_TRACKED_NOTIFICATIONS) {
    const oldest = notificationIdOrder.shift()!;
    seenNotificationIds.delete(oldest);
  }
  return false;
};

export const getPermissionsUtility = (
  permissions: any,
  tenant: any,
  orgsDropDownDataVal: any,
) => {
  const currentStatePermissions = permissions.find(
    (res: any) => res.tenantId === tenant,
  );
  const data = currentStatePermissions.resourceDetails.filter((res: any) => {
    const isResourceInDropdown = orgsDropDownDataVal.some(
      (val: any) => res.resourceName === val.text,
    );
    if (isResourceInDropdown) {
      return {
        resources: res.resources,
        resourceName: res.resourceName,
        permissions: res.permissions,
      };
    }
  });
  return data;
};

export const getOrgDataUtility = (permissions: any, tenant: any) => {
  let orgsDropDownDataVal: any = [];
  _.map(permissions, (val: any) => {
    if (val.tenantId === tenant) {
      orgsDropDownDataVal = val.resourceDetails.map((res: any) => {
        // Do something with res
        return {
          value: res?.resources,
          text:
            res?.resourceName === "" || res?.resourceName === null
              ? res?.resources
              : res?.resourceName,
        };
      });
    }
  });
  return orgsDropDownDataVal;
};

export const handleRefresh = (
  isRefreshed: boolean,
  dispatch: any,
  setToken?: any,
  selectedView?: string,
) => {
  if (isRefreshed == true) AuthProvider().clearCookie("XSRF-TOKEN");
  else if (selectedView === "Dashboard") {
    setToken("refreshed");
  }
  AuthProvider()
    .getRefreshToken()
    .then((resp: any) => {
      const tokenData = resp?.access_token;
      selectedView === "Dashboard" && setToken("notRefreshed");
      if (tokenData && resp && isRefreshed == true) {
        dispatch(
          getCSRFToken({
            authorization: localStorage.getItem("access_token"),
          }),
        );
      }
    });
};

export const closeConnection = async (
  signalRConnection: any,
  HubConnectionState: any,
  dispatch: any,
) => {
  if (
    signalRConnection &&
    signalRConnection.state === HubConnectionState.Connected
  ) {
    await signalRConnection.stop();
    dispatch(setSignalR(null));
    // connection
    // signalRConnection?.onclose((error: any) => {
    //   // console.error(error);
    // });
  }
};

export const setUpSignalRConnection = async (
  orgName: any,
  dispatch: any,
  setSignalrConnectionOrRefreshCallback?: any,
  isAcademyContext?: boolean,
  isFromEditCourse?: boolean,
  courseId?: string | null,
  courseDefData?: any,
) => {
  const word = "...";
  // Build URL based on context
  let connectionUrl = `${config.API_SERVICE}/notificationhub?siteId=${orgName}`;
  // Only add userId for academy context
  if (isAcademyContext && !isFromEditCourse) {
    let userId = "";
    try {
      const accessToken = localStorage.getItem("access_token");
      if (accessToken) {
        // Decode JWT token to get user information
        const tokenPayload = JSON.parse(atob(accessToken.split(".")[1]));
        userId =
          tokenPayload?.sub ||
          tokenPayload?.userId ||
          tokenPayload?.nameid ||
          "";
      }
    } catch (error) {
      // Token decode failed
    }

    if (userId) {
      connectionUrl += `&userId=${userId}`;
    }
  }

  let connection: HubConnection;
  connection = new HubConnectionBuilder()
    .withUrl(connectionUrl, {
      // accessTokenFactory: () => AuthProvider().getAccessToken(),
    })
    .build();

  // Handle both old signature (setSignalrConnection) and new signature (refreshCallback)
  const isRefreshCallback =
    typeof setSignalrConnectionOrRefreshCallback === "function" &&
    isAcademyContext &&
    !isFromEditCourse;
  if (!isRefreshCallback && setSignalrConnectionOrRefreshCallback) {
    setSignalrConnectionOrRefreshCallback(connection); // Old signature: setSignalrConnection
  }
  dispatch(setSignalR(connection));
  connection.on("PushMessage", (user: any, notification: INotification) => {
    if (isDuplicateNotification(notification.notificationId)) return;
    if (notification.message) {
      if (!notification.message.includes(word)) {
        // check if notification to show or not and then getorgstatus 0
        customToast(
          notification.message,
          notification && notification.severity
            ? notification.severity
            : "critical",
        );
      }
      if (notification.vmId) {
        const messageHave = notification.message.split(" ");

        const requiredWords = ["Snapshot", "restored", "successfully"];
        const allWordsPresent = requiredWords.every((word) =>
          messageHave.includes(word),
        );
        if (allWordsPresent) {
          dispatch(setVmIdNotification(notification.vmId));
        }
      }
      // START - Academy SignalR handling for edit course page
      const notifMessage = notification.message.toLowerCase();
      const isPowerOffInProgress =
        notification.vmId && notifMessage.includes("powering off...");
      const isPowerOnInProgress =
        notification.vmId && notifMessage.includes("powering on...");
      const isMountingInProgress =
        notification.vmId && notifMessage.includes("inserting media...");
      const isDismountingInProgress =
        notification.vmId && notifMessage.includes("ejecting media...");
      const isMediaOperationSuccess =
        notification.severity === "success" &&
        notification.vmId &&
        (notifMessage.includes("media inserted") ||
          notifMessage.includes("media ejected"));
      const isPowerOperationSuccess =
        notification.severity === "success" &&
        notification.vmId &&
        (notifMessage.includes("powered off successfully") ||
          notifMessage.includes("powered on successfully"));
      const isPowerOnActionFailed =
        notification.vmId &&
        notifMessage.includes("power on action") &&
        notifMessage.includes("not completed");
      const isPowerOffActionFailed =
        notification.vmId &&
        notifMessage.includes("power off action") &&
        notifMessage.includes("not completed");
      if (
        isAcademyContext &&
        isFromEditCourse &&
        notification.vmId &&
        courseId
      ) {
        const rawVmId = notification.vmId?.replace(/^vm-/, "") ?? "";
        const vmName =
          courseDefData?.vmDetails?.find((vm: any) => vm.vcdVmID === rawVmId)
            ?.vmName || notification.vmId;
        if (isPowerOffInProgress || isPowerOnInProgress) {
          let msg = isPowerOffInProgress
            ? `"${vmName}" is powering off, please wait.`
            : `"${vmName}" is powering on, please wait.`;
          customToast(msg, "information");
          dispatch(getCourseDataById(courseId));
        } else if (isPowerOperationSuccess) {
          dispatch(getCourseDataById(courseId));
        } else if (isPowerOnActionFailed) {
          dispatch(getCourseDataById(courseId));
        } else if (isPowerOffActionFailed) {
          dispatch(getCourseDataById(courseId));
        } else if (isMountingInProgress || isDismountingInProgress) {
          let msg = isMountingInProgress
            ? `"${vmName}" media is being inserted, please wait.`
            : `"${vmName}" media is being ejected, please wait.`;
          customToast(msg, "information");
          dispatch(getCourseDataById(courseId));
        } else if (isMediaOperationSuccess) {
          dispatch(getCourseDataById(courseId));
        }
      }
      // END - Academy SignalR handling for edit course page
      dispatch(setLoading(true));
      // Conditionally call APIs based on context
      if (isAcademyContext && !isFromEditCourse) {
        // For academy dashboard, use callback to refresh with correct selectedCard state
        dispatch(getCardCourseCount());
        if (isRefreshCallback && setSignalrConnectionOrRefreshCallback) {
          setSignalrConnectionOrRefreshCallback(); // Call refresh callback with correct parameters
        }
      } else {
        dispatch(getOrgStatus(0));
      }
    }
  });
  connection.start().then(() => {
    // Only call essential APIs on connection start - components handle their own data loading
    if (isAcademyContext && !isFromEditCourse) {
      dispatch(getCardCourseCount()); // Only refresh card counts, let component handle course data
    } else {
      dispatch(getOrgStatus(0));
    }
  });
};

export const setParams = (
  siteId: any,
  tenantId: any,
  urlDashboard: any,
  referrer?: any,
) => {
  siteId !== null &&
    siteId !== undefined &&
    localStorage.setItem("siteId", siteId);
  tenantId !== null &&
    tenantId !== undefined &&
    localStorage.setItem("tenantId", tenantId);
  urlDashboard !== null &&
    urlDashboard !== undefined &&
    urlDashboard === "dashboard" &&
    localStorage.setItem("urlDashboard", urlDashboard);
  if (typeof referrer !== "undefined") {
    if (referrer !== null) {
      localStorage.setItem("referrer", referrer);
    } else {
      localStorage.removeItem("referrer");
    }
  }
};

export const removeItemMetaData = () => {
  localStorage.removeItem("siteId");
  localStorage.removeItem("tenantId");
  localStorage.removeItem("urlDashboard");
};
